import { firstNameProblem, fullName, type Appearance, type OfferedInvitation } from "odyssey-shared";
import { say } from "../words.ts";

/**
 * What character select shows and how it moves (P1.4-T6 and P1.4-T28, ADR-065 decisions 5 and 6): the
 * account's characters in creation order, each listed by its full name, the one selected, its character limit,
 * whether the screen lists them or asks for a new character's first name, what is typed there, the one note
 * the screen shows, whether a request is out, and what takes focus after a move. It is pure data moved by
 * events, so every move the screen makes is decided here and unit-tested without a page; the screen draws a
 * state and the page sends the requests and the events.
 *
 * The screen opens on the characters a login answered, with the first selected. Enter the world asks the
 * gateway for the selected character's session token (a refresh naming it), and the note shows the busy words
 * until the answer, or the answer's sentence when it refuses. While the account holds fewer characters than
 * its limit, Create a character asks for a first name alone, since the family already stands, then the Look step
 * with the voice (ADR-094 decision 23), whose Enter the world creates the character; Back returns a step. A first
 * name that breaks the rule is refused before it is sent with the gateway's own sentence; a first name the gateway
 * refuses returns from the Look step to the first name with the look kept; a creation the gateway refuses at the
 * limit returns to the list and offers creation no more. Change look opens the Look panel for the selected
 * character with its look and voice (ADR-094 decision 29 and note 128), and an accepted change returns to the list
 * with the character's new look and voice.
 */

/**
 * One character of the account as character select lists it: its id, which the page names in the refresh
 * that chooses it, its first name, its voice (P1.9-T83), absent or null for none, and its look (ADR-094 decision
 * 27), absent when the list carries none.
 */
export interface RosterCharacter {
  readonly id: string;
  readonly firstName: string;
  readonly voice?: string | null;
  readonly look?: Appearance;
  readonly partyInvites?: OfferedInvitation;
}

/**
 * A note: its sentence and whether it reports a failure (drawn as an error) or not (drawn as a hint). It sits
 * under the list while the screen lists the characters, under the first name while it asks for one, and over the
 * rows on the Look step and in Change look.
 */
export interface SelectNote {
  readonly text: string;
  readonly tone: "info" | "error";
}

/**
 * Whether the screen lists the characters (`choose`), asks for a new character's first name (`create`), shows the
 * new character's Look step (`look`), or changes the selected character's look and voice (`change`).
 */
export type SelectMode = "choose" | "create" | "look" | "change";

/**
 * A change of look in progress at character select: the character, the look and voice shown, and the look and voice
 * it stood in when the change opened, which Confirm compares with.
 */
export interface SelectChange {
  readonly id: string;
  readonly look: Appearance;
  readonly voice: string | null;
  readonly from: { readonly look: Appearance | null; readonly voice: string | null };
}

/**
 * The screen's state: the mode; the account's family name, its characters in creation order and its
 * character limit (null from a gateway before the limit, which offers no creation); the id of the character
 * selected (null only when there are none); the first name typed for a new character, the voice picked for it, null
 * for none, and its look, null until its Look step first opens; the change of look in progress, or null; the note;
 * whether a request is out; whether the gateway answered that the account is at its limit; and what a move asks to
 * focus, the list or the first name, or null when it leaves focus where it is.
 */
export interface SelectState {
  readonly mode: SelectMode;
  readonly familyName: string;
  readonly characters: readonly RosterCharacter[];
  readonly limit: number | null;
  readonly selected: string | null;
  readonly firstName: string;
  readonly voice: string | null;
  readonly look: Appearance | null;
  readonly change: SelectChange | null;
  readonly note: SelectNote | null;
  readonly busy: boolean;
  readonly full: boolean;
  readonly focus: "list" | "firstName" | null;
}

/**
 * What moves the screen:
 *
 * - `chose`: the player selected a character of the list.
 * - `sent`: Enter the world went out, or a creation, or a change of look; `text` is the busy words, shown until the
 *   answer.
 * - `refused`: the answer was a refusal, whose sentence the screen shows where the player is.
 * - `nameRefused`: the gateway refused the new character's first name; the Look step returns to it.
 * - `creating`: Create a character.
 * - `typed`: the player changed the new character's first name.
 * - `looking`: the first name passed the page's check and the Look step opens; `look` is the look it opens on the
 *   first time, kept after that.
 * - `looked`: the player changed the look on the Look step or in Change look.
 * - `voiced`: the player picked a voice, null for none, on the Look step or in Change look.
 * - `changing`: Change look for the selected character; `look` is the look it opens on.
 * - `changed`: the gateway accepted a change of look; the character now stands in `look` and `voice`.
 * - `told`: the page has something to say that is no refusal, as that a voice has no sample to preview.
 * - `invalid`: the page refused the first name before sending, since it breaks the rule.
 * - `back`: Back: from the Look step to the first name, from the first name or Change look to the list.
 * - `full`: the gateway refused a creation because the account is at its limit; `text` is its sentence.
 */
export type SelectEvent =
  | { readonly type: "chose"; readonly id: string }
  | { readonly type: "sent"; readonly text: string }
  | { readonly type: "refused"; readonly text: string }
  | { readonly type: "nameRefused"; readonly text: string }
  | { readonly type: "creating" }
  | { readonly type: "typed"; readonly value: string }
  | { readonly type: "looking"; readonly look: Appearance }
  | { readonly type: "looked"; readonly look: Appearance }
  | { readonly type: "voiced"; readonly voice: string | null }
  | { readonly type: "changing"; readonly look: Appearance }
  | { readonly type: "changed"; readonly id: string; readonly look: Appearance; readonly voice: string | null }
  | { readonly type: "told"; readonly text: string }
  | { readonly type: "invalid" }
  | { readonly type: "back" }
  | { readonly type: "full"; readonly text: string };

/**
 * The state the screen opens in: the characters a login answered, in the order sent, the first selected and
 * the list asked to take focus.
 *
 * @param familyName The account's family name.
 * @param characters The account's characters in creation order.
 * @param limit The account's character limit, or null when the gateway gave none.
 * @returns The state.
 */
export function initialSelectState(familyName: string, characters: readonly RosterCharacter[], limit: number | null = null): SelectState {
  return { mode: "choose", familyName, characters, limit, selected: characters[0]?.id ?? null, firstName: "", voice: null, look: null, change: null, note: null, busy: false, full: false, focus: "list" };
}

/**
 * Whether the screen offers creation: the gateway gave a limit, the account holds fewer characters than it,
 * and no creation was refused at it since the screen opened (ADR-065 decision 6).
 *
 * @param state The screen's state.
 * @returns True when Create a character is offered.
 */
export function canCreate(state: SelectState): boolean {
  return !state.full && state.limit !== null && state.characters.length < state.limit;
}

/**
 * What the list shows under the characters (ADR-061's amendment "character select as redesigned"): while creation is
 * offered, New character with the slots used of the account's limit; at the limit, or once a creation was refused
 * at it, the line saying every slot is in use; and nothing when the gateway gave no limit.
 */
export type SlotRow = { readonly kind: "room"; readonly used: number; readonly limit: number } | { readonly kind: "full"; readonly limit: number };

/**
 * The row the list shows under the characters, from the account's own limit.
 *
 * @param state The screen's state.
 * @returns New character with its count, the line at the limit, or null when the gateway gave no limit.
 */
export function slotRow(state: SelectState): SlotRow | null {
  if (state.limit === null) {
    return null;
  }
  return canCreate(state) ? { kind: "room", used: state.characters.length, limit: state.limit } : { kind: "full", limit: state.limit };
}

/**
 * Whether the page refuses the new character's first name before sending: trimmed, as it is sent, it breaks
 * the first-name rule the gateway applies (ADR-065 decision 2). Whether another character holds it is the
 * gateway's to judge.
 *
 * @param state The screen's state, asking for a first name.
 * @returns True when the first name is refused.
 */
export function firstNameRefused(state: SelectState): boolean {
  return firstNameProblem(state.firstName.trim()) !== null;
}

/**
 * Moves the screen by one event. While a request is out, a choice, a second send, Create a character, Change look
 * and Back change nothing, and neither do a look or a voice picked, as the page refuses them.
 *
 * - A choice selects a character the list holds, keeping the note; an id it does not hold changes nothing.
 * - A send shows the busy words.
 * - A refusal ends the wait and shows its sentence as an error, keeping the choice, what was typed and the look and
 *   voice shown, and focuses the first name while the screen asks for one.
 * - A first name the gateway refused returns from the Look step to the first name with its sentence as an error, the
 *   look and voice kept, and focuses it.
 * - Create a character, while it is offered, asks for a first name with nothing typed, no voice and no look, and
 *   focuses it.
 * - Typing changes the first name and moves no focus.
 * - The Look step opens from the first name, on the look it had or the one given; a look or a voice picked changes
 *   the new character's on the Look step and the change's in Change look, moves no focus and clears a note that
 *   was no error.
 * - Change look opens for the selected character on the look given and the voice it has; an accepted change returns
 *   to the list with the character's look and voice replaced, and focuses it.
 * - Something told shows as a hint and ends no wait.
 * - A first name refused before sending shows the gateway's sentence as an error and takes focus.
 * - Back returns a step: from the Look step to the first name, focused; from the first name or Change look to the
 *   list, focused, with no note, keeping the choice.
 * - A creation refused at the limit ends the wait, returns to the list with its sentence as an error,
 *   focuses the list and offers creation no more.
 *
 * @param state The state.
 * @param event What happened.
 * @returns The next state.
 */
export function selectReducer(state: SelectState, event: SelectEvent): SelectState {
  switch (event.type) {
    case "chose":
      if (state.busy || state.selected === event.id || !state.characters.some((character) => character.id === event.id)) {
        return state;
      }
      return { ...state, selected: event.id };
    case "sent":
      if (state.busy) {
        return state;
      }
      return { ...state, busy: true, note: { text: event.text, tone: "info" }, focus: null };
    case "refused":
      return { ...state, busy: false, note: { text: event.text, tone: "error" }, focus: state.mode === "create" ? "firstName" : null };
    case "nameRefused":
      return { ...state, mode: state.mode === "look" ? "create" : state.mode, busy: false, note: { text: event.text, tone: "error" }, focus: "firstName" };
    case "creating":
      if (state.busy || state.mode !== "choose" || !canCreate(state)) {
        return state;
      }
      return { ...state, mode: "create", firstName: "", voice: null, look: null, note: null, focus: "firstName" };
    case "typed":
      return { ...state, firstName: event.value, focus: null };
    case "looking":
      if (state.busy || state.mode !== "create") {
        return state;
      }
      return { ...state, mode: "look", look: state.look ?? event.look, note: null, focus: null };
    case "looked":
      if (state.busy) {
        return state;
      }
      if (state.mode === "look") {
        return { ...state, look: event.look, note: kept(state.note), focus: null };
      }
      return state.mode === "change" && state.change !== null ? { ...state, change: { ...state.change, look: event.look }, note: kept(state.note), focus: null } : state;
    case "voiced":
      if (state.busy) {
        return state;
      }
      if (state.mode === "look") {
        return { ...state, voice: event.voice, note: kept(state.note), focus: null };
      }
      return state.mode === "change" && state.change !== null ? { ...state, change: { ...state.change, voice: event.voice }, note: kept(state.note), focus: null } : state;
    case "changing": {
      const chosen = chosenCharacter(state);
      if (state.busy || state.mode !== "choose" || chosen === null) {
        return state;
      }
      const voice = chosen.voice ?? null;
      return { ...state, mode: "change", change: { id: chosen.id, look: event.look, voice, from: { look: chosen.look ?? null, voice } }, note: null, focus: null };
    }
    case "changed":
      return { ...state, mode: "choose", busy: false, change: null, characters: state.characters.map((character) => (character.id === event.id ? { ...character, look: event.look, voice: event.voice } : character)), note: null, focus: "list" };
    case "told":
      return { ...state, note: { text: event.text, tone: "info" }, focus: null };
    case "invalid":
      if (state.busy) {
        return state;
      }
      return { ...state, note: { text: say(firstNameProblem(state.firstName.trim()) === "reserved" ? "account.refused.firstNameStaffWord" : "account.refused.firstNameInvalid"), tone: "error" }, focus: "firstName" };
    case "back":
      if (state.busy || state.mode === "choose") {
        return state;
      }
      if (state.mode === "look") {
        return { ...state, mode: "create", note: null, focus: "firstName" };
      }
      return { ...state, mode: "choose", change: null, note: null, focus: "list" };
    case "full":
      return { ...state, mode: "choose", busy: false, full: true, note: { text: event.text, tone: "error" }, focus: "list" };
  }
}

/**
 * The note a pick keeps: an error stays until the next send, anything else goes.
 *
 * @param note The note shown.
 * @returns The note kept, or null.
 */
function kept(note: SelectNote | null): SelectNote | null {
  return note?.tone === "error" ? note : null;
}

/**
 * Whether Change look's Confirm would change anything: the look or the voice shown differs from what the character
 * stood in when the change opened.
 *
 * @param change The change in progress.
 * @returns True when Confirm has something to send.
 */
export function changeDiffers(change: SelectChange): boolean {
  const from = change.from.look;
  return change.voice !== change.from.voice || from === null || from.skin !== change.look.skin || from.hair !== change.look.hair || from.clothes !== change.look.clothes;
}

/**
 * The character Enter the world plays: the selected row.
 *
 * @param state The screen's state.
 * @returns The character, or null when the list is empty.
 */
export function chosenCharacter(state: SelectState): RosterCharacter | null {
  return state.characters.find((character) => character.id === state.selected) ?? null;
}

/**
 * How the list names a character: its full name, the first name and the family name joined by one space
 * ("Kaelen Ashgrove"), as everyone sees it (ADR-065 decision 1).
 *
 * @param character The character.
 * @param familyName The account's family name.
 * @returns The label.
 */
export function characterLabel(character: RosterCharacter, familyName: string): string {
  return fullName(character.firstName, familyName);
}
